import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, History, Loader2, PanelRight, Pencil, Rocket } from 'lucide-react';
import { toast } from 'sonner';
import { P, type CampaignSpec } from '@vagusx/shared';
import { refusalFrom, type Refusal } from './refusal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { PageTabsList } from '@/components/custom/page-tabs-list';
import {
  useAudienceCatalog,
  useContactPolicy,
  useCreateCampaign,
  useOutreachCampaign,
  usePublishCampaign,
  useSetCampaignStatus,
  useUpdateCampaign,
  useWorkflowOptions,
} from '@/hooks/use-outreach';
import { useHasPermission, useIsAdmin } from '@/hooks/use-permissions';
import { LaunchStep } from './LaunchStep';
import { RehearseStep } from './RehearseStep';
import type { StepContext } from './StepCard';
import { StepsStep, type CampaignDetails } from './StepsStep';
import { TextPanel } from './TextPanel';
import { VersionsDialog } from './VersionsDialog';
import { WhoStep } from './WhoStep';
import { callingHours, defaultSpec, fitStepDays, withContinueOn } from './spec';
import { useFilterNames } from './use-filter-names';

/**
 * The campaign editor (ADR-318, G), for a new campaign and an existing one
 * alike, in the prototype's flow: Who → Steps → Rehearse → Launch (P11). The
 * English description and its compiler (ADR-155) stay, as a side panel.
 *
 * There is no create dialog. In create mode the row is written when the
 * campaign is first published, so the whole editor works — audience counts and
 * the call calendar included — before anything exists in the database.
 *
 * **Publish is the save**, and the only one. There is no draft-spec storage: a
 * run pins the version it started on, so a half-finished edit must not reach a
 * patient who is three steps in. **Launch** is publish, then activate; the
 * server re-runs the launch check on activate and refuses with every failure.
 */

const NEW_DETAILS: CampaignDetails = {
  name: '',
  description: '',
  priority: 50,
  endWhenNoLongerQualifies: false,
};

const STAGES = [
  { value: 'who', label: 'Who' },
  { value: 'steps', label: 'Steps' },
  { value: 'rehearse', label: 'Rehearse' },
  { value: 'launch', label: 'Launch' },
] as const;
type Stage = (typeof STAGES)[number]['value'];

export function CampaignEditor({
  campaignId,
  onClose,
  onSaved,
  onLaunched,
}: {
  /** Absent in create mode — nothing exists in the database yet. */
  campaignId?: string;
  onClose: () => void;
  /** After a publish or a save; a new campaign's id is first known here. */
  onSaved: (id: string) => void;
  /** After a launch that started the campaign; defaults to `onSaved`. */
  onLaunched?: (id: string) => void;
}): React.ReactElement {
  const isNew = !campaignId;
  const { data: campaign, isLoading } = useOutreachCampaign(campaignId);
  const { data: catalog } = useAudienceCatalog();
  const { data: policy } = useContactPolicy();
  const { data: workflowOptions } = useWorkflowOptions();
  const create = useCreateCampaign();
  const update = useUpdateCampaign();
  const setStatus = useSetCampaignStatus();
  // Activate shows a refusal on the Launch screen, so it is not also toasted.
  const activation = useSetCampaignStatus({ quiet: true });
  const publish = usePublishCampaign();
  const canRun = useHasPermission(P.OUTREACH_RUN);
  const isOps = useIsAdmin();
  const { names: filterNames, ready: filterNamesReady } = useFilterNames();

  // A new campaign schedules referrals: the one purpose with audience data
  // behind it today (P11).
  const [spec, setSpecRaw] = useState<CampaignSpec | null>(
    isNew ? defaultSpec('open_referral') : null
  );
  const [details, setDetails] = useState<CampaignDetails | null>(isNew ? NEW_DETAILS : null);
  const [stage, setStage] = useState<Stage>(isNew ? 'who' : 'steps');
  /** The one open step on the rail. */
  const [openStepId, setOpenStepId] = useState<string>('');
  /** The one open row in the campaign-wide settings band. */
  const [openRuleId, setOpenRuleId] = useState<string>('');
  const [focusedSentenceId, setFocusedSentenceId] = useState<string>('');
  const [editingName, setEditingName] = useState(isNew);
  const [textOpen, setTextOpen] = useState(true);
  const [versionsOpen, setVersionsOpen] = useState(false);
  /** Why the server refused a publish or an activate (422), shown on Launch. */
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  /**
   * In create mode after a publish: the spec and details that publish wrote.
   * The editor is still `isNew` (a refused launch keeps it here), where every
   * spec reads as dirty; comparing to this stops a retried Launch minting an
   * identical version each time.
   */
  const [publishedSnapshot, setPublishedSnapshot] = useState<string | null>(null);
  /**
   * The row a previous save already created, when its publish then failed.
   * Create and publish are two calls; without this, pressing Publish again
   * would re-run `create` with the same name and fail on the unique index.
   */
  const [createdId, setCreatedId] = useState<string | null>(null);

  /**
   * Every edit goes through here. The editor writes `continueOn` on every step
   * and never shows it (R1), and keeps every step's days inside the campaign's
   * when those change — a step left calling on a day the campaign dropped is
   * refused at launch (`step_days_outside_window`).
   */
  const setSpec = (next: CampaignSpec): void => {
    setSpecRaw(withContinueOn(fitStepDays(next)));
    setRefusal(null);
  };

  // Seeded once, not on every arrival of server data: publishing refetches the
  // campaign, and re-seeding there would discard whatever the author has typed
  // since.
  useEffect(() => {
    if (!campaign) return;
    setSpecRaw((current) => current ?? campaign.spec ?? defaultSpec('open_referral'));
    setDetails(
      (current) =>
        current ?? {
          name: campaign.name,
          description: campaign.description ?? '',
          priority: campaign.priority,
          endWhenNoLongerQualifies: campaign.endWhenNoLongerQualifies,
        }
    );
  }, [campaign]);

  /** A step that no longer exists cannot stay open. */
  useEffect(() => {
    if (!spec) return;
    setOpenStepId((current) =>
      current === '' || spec.sequence.some((s) => s.id === current) ? current : ''
    );
  }, [spec]);

  const serverDetails: CampaignDetails | null = campaign
    ? {
        name: campaign.name,
        description: campaign.description ?? '',
        priority: campaign.priority,
        endWhenNoLongerQualifies: campaign.endWhenNoLongerQualifies,
      }
    : null;

  // Tracked apart because they save differently: the spec becomes an immutable
  // version, the details are a plain update. A rename must not mint a version.
  const specDirty = useMemo(
    () => isNew || JSON.stringify(spec) !== JSON.stringify(campaign?.spec ?? null),
    [isNew, spec, campaign]
  );
  const detailsDirty = useMemo(
    () => isNew || JSON.stringify(details) !== JSON.stringify(serverDetails),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isNew, details, serverDetails]
  );
  const dirty = (specDirty || detailsDirty) && !!spec && !!details;

  const context: StepContext | null = useMemo(
    () =>
      spec
        ? {
            workflowOptions,
            pins: new Map((campaign?.workflowPins ?? []).map((p) => [p.stepId, p])),
            audienceType: spec.audience.type,
            campaignDays: spec.window.days,
            callingHours: callingHours(spec.window, policy),
            personaId: spec.personaId,
            showStack: isOps,
          }
        : null,
    [spec, workflowOptions, campaign?.workflowPins, isOps, policy]
  );

  if ((!isNew && isLoading) || !spec || !details || !context) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const catalogEntry = catalog?.find((c) => c.type === spec.audience.type);
  const saving =
    create.isPending ||
    publish.isPending ||
    update.isPending ||
    setStatus.isPending ||
    activation.isPending;
  const status = campaign?.status ?? 'draft';
  const stageIndex = STAGES.findIndex((s) => s.value === stage);

  /**
   * Hold the spec as the server stored it. Publish pins every step's workflow
   * version, so the editor's copy (with `versionId: null`) would otherwise
   * never equal the published one and stay "unpublished" for ever.
   */
  const adoptPublished = (published: { spec: CampaignSpec | null }): void => {
    if (published.spec) setSpecRaw(published.spec);
  };

  /** Write whatever changed; returns the campaign's id. */
  const save = async (): Promise<string> => {
    const detailFields = {
      name: details.name.trim(),
      description: details.description.trim() || null,
      priority: details.priority,
      endWhenNoLongerQualifies: details.endWhenNoLongerQualifies,
    };

    if (isNew) {
      let id = createdId;
      if (id) {
        // Retrying after a publish that failed: the row exists, so write what
        // the author changed while fixing the problem.
        await update.mutateAsync({ id, data: detailFields });
      } else {
        const created = await create.mutateAsync({
          name: detailFields.name,
          description: detailFields.description,
          priority: detailFields.priority,
        });
        id = created.id;
        setCreatedId(id);
        if (detailFields.endWhenNoLongerQualifies) {
          await update.mutateAsync({ id, data: { endWhenNoLongerQualifies: true } });
        }
      }
      const published = await publish.mutateAsync({ id, data: { spec } });
      adoptPublished(published);
      setPublishedSnapshot(JSON.stringify({ spec: published.spec ?? spec, details }));
      return id;
    }

    if (detailsDirty) await update.mutateAsync({ id: campaignId, data: detailFields });
    if (specDirty) adoptPublished(await publish.mutateAsync({ id: campaignId, data: { spec } }));
    return campaignId;
  };

  /**
   * Show a refusal on the Launch screen. True when it was one (launch checks
   * or field errors); anything else stays with the global error toast.
   */
  const showRefusal = (err: unknown): boolean => {
    const found = refusalFrom(err);
    if (!found) return false;
    setRefusal(found);
    setStage('launch');
    return true;
  };

  /** Save, or null when the server refused (shown) or failed (toasted). */
  const trySave = async (): Promise<string | null> => {
    try {
      return await save();
    } catch (err) {
      showRefusal(err);
      return null;
    }
  };

  const saveOnly = async (): Promise<void> => {
    if (!details.name.trim()) return;
    const id = await trySave();
    if (id) onSaved(id);
  };

  /** Activate, catching a refusal's launch checks to show on the Launch screen. */
  const activate = async (id: string): Promise<boolean> => {
    try {
      await activation.mutateAsync({ id, status: 'active' });
      setRefusal(null);
      return true;
    } catch (err) {
      // Not a refusal (a network error, a 500): toast it, as the global handler would.
      if (!showRefusal(err)) toast.error(err instanceof Error ? err.message : 'Could not start');
      return false;
    }
  };

  /** Launch: publish what changed, then start it. Publishing over a running campaign is the launch. */
  const launch = async (): Promise<void> => {
    if (!details.name.trim()) {
      setEditingName(true);
      return;
    }
    // A new campaign whose launch was refused already exists; with nothing
    // changed since it was published, retrying only activates it.
    const unchangedSincePublish =
      isNew && createdId !== null && JSON.stringify({ spec, details }) === publishedSnapshot;
    const id = !dirty || unchangedSincePublish ? (campaignId ?? createdId) : await trySave();
    if (!id) return;
    const started = status === 'active' || (await activate(id));
    // A refused launch stays in the editor with its checks showing: `onSaved`
    // moves to the campaign's page (and remounts a new campaign's editor),
    // losing them. A new one's retry path (`createdId`) picks it up from here.
    if (started) (onLaunched ?? onSaved)(id);
  };

  /**
   * Open the control a sentence in the text panel describes. Sentence ids are
   * the describer's; one this does not recognise records the highlight and
   * opens nothing.
   */
  const focusSentence = (sentenceId: string): void => {
    setFocusedSentenceId(sentenceId);
    if (sentenceId.startsWith('audience')) {
      setStage('who');
      return;
    }
    if (sentenceId.startsWith('step.')) {
      const stepId = sentenceId.split('.')[1];
      if (stepId && spec.sequence.some((s) => s.id === stepId)) {
        setStage('steps');
        setOpenStepId(stepId);
      }
      return;
    }
    const RULE_ROWS: Record<string, string> = {
      'ending.success': 'success',
      'ending.stop': 'stop',
      'ending.limits': 'limits',
      hours: 'hours',
    };
    const row = RULE_ROWS[sentenceId];
    if (row) {
      setStage('steps');
      setOpenRuleId(row);
    }
  };

  // Every stage stays mounted (forceMount) so what an author was in the middle
  // of — a filter row with no value yet, a calendar's chosen location —
  // survives switching stages; only the current one is shown.
  const hiddenUnless = (value: Stage): string | undefined =>
    stage === value ? undefined : 'hidden';

  const launchLabel = status === 'active' ? 'Publish changes' : 'Launch campaign';

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="flex shrink-0 items-start justify-between gap-6 border-b px-6 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="mt-1 shrink-0"
              onClick={onClose}
              aria-label="Back to campaigns"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0 flex-1">
              {editingName ? (
                <>
                  <Label htmlFor="campaign-name" className="sr-only">
                    Campaign name
                  </Label>
                  <Input
                    id="campaign-name"
                    autoFocus
                    value={details.name}
                    maxLength={120}
                    placeholder="Name this campaign"
                    className="h-9 text-xl font-semibold"
                    onChange={(e) => setDetails({ ...details, name: e.target.value })}
                    onBlur={() => details.name.trim() && setEditingName(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && details.name.trim()) setEditingName(false);
                      if (e.key === 'Escape') setEditingName(false);
                    }}
                  />
                </>
              ) : (
                <button
                  type="button"
                  className="group/name flex min-w-0 items-center gap-2 text-left"
                  onClick={() => setEditingName(true)}
                >
                  <span className="truncate text-xl font-semibold">
                    {details.name || 'Untitled campaign'}
                  </span>
                  <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/name:opacity-100" />
                </button>
              )}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {campaign?.version != null && <span>v{campaign.version} published</span>}
                {!isNew && specDirty && (
                  <Badge variant="outline" className="text-xs">
                    Unpublished changes
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 pt-1">
            {/*
              Status is an operational control on a live campaign, not part of
              the versioned document, so it acts on the spot. Activating here
              runs the same server check as Launch.
            */}
            {/* Without outreach:run the status is shown, not changeable. */}
            {!isNew && campaign && !canRun && (
              <Badge variant="outline" className="capitalize">
                {campaign.status}
              </Badge>
            )}
            {!isNew && campaign && canRun && (
              <Select
                value={campaign.status}
                onValueChange={(next) => {
                  if (next === 'active') void activate(campaign.id);
                  if (next === 'paused') setStatus.mutate({ id: campaign.id, status: 'paused' });
                }}
              >
                <SelectTrigger className="w-28" aria-label="Campaign status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft" disabled>
                    Draft
                  </SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            )}
            {!isNew && (
              <Button variant="outline" onClick={() => setVersionsOpen(true)}>
                <History className="mr-2 h-4 w-4" />
                Versions
              </Button>
            )}
            <Button
              variant={textOpen ? 'secondary' : 'outline'}
              onClick={() => setTextOpen((open) => !open)}
              aria-pressed={textOpen}
            >
              <PanelRight className="mr-2 h-4 w-4" />
              Text
            </Button>
            {/*
              Publishing without starting: a rename is a plain save, and a spec
              change becomes a version that a paused or draft campaign keeps
              until someone launches it.
            */}
            <Button
              variant="outline"
              disabled={!dirty || saving || !details.name.trim()}
              onClick={() => void saveOnly()}
            >
              {specDirty ? 'Publish' : 'Save'}
            </Button>
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <div className="flex min-h-0 flex-1">
          <Tabs
            value={stage}
            onValueChange={(value) => setStage(value as Stage)}
            className="flex min-w-0 flex-1 flex-col"
          >
            <PageTabsList className="shrink-0 px-6" aria-label="Campaign editor steps">
              {STAGES.map((s, i) => (
                <TabsTrigger key={s.value} value={s.value}>
                  <span className="mr-1.5 tabular-nums text-muted-foreground">{i + 1}</span>
                  {s.label}
                </TabsTrigger>
              ))}
            </PageTabsList>

            <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
              <div className="mx-auto max-w-6xl">
                <TabsContent value="who" forceMount className={hiddenUnless('who')}>
                  <WhoStep spec={spec} onSpecChange={setSpec} catalog={catalog} />
                </TabsContent>
                <TabsContent value="steps" forceMount className={hiddenUnless('steps')}>
                  <StepsStep
                    spec={spec}
                    onSpecChange={setSpec}
                    details={details}
                    onDetailsChange={setDetails}
                    policy={policy}
                    catalogEntry={catalogEntry}
                    context={context}
                    openStepId={openStepId}
                    onOpenStepChange={setOpenStepId}
                    openRuleId={openRuleId}
                    onOpenRuleChange={setOpenRuleId}
                    active={stage === 'steps'}
                  />
                </TabsContent>
                <TabsContent value="rehearse" forceMount className={hiddenUnless('rehearse')}>
                  <RehearseStep
                    spec={spec}
                    workflowOptions={workflowOptions}
                    active={stage === 'rehearse'}
                  />
                </TabsContent>
                <TabsContent value="launch" forceMount className={hiddenUnless('launch')}>
                  <LaunchStep spec={spec} refusal={refusal} active={stage === 'launch'} />
                </TabsContent>

                <div className="mt-8 flex items-center justify-end gap-2 border-t pt-4">
                  {stageIndex > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => setStage(STAGES[stageIndex - 1]!.value)}
                    >
                      Back
                    </Button>
                  )}
                  {stageIndex < STAGES.length - 1 ? (
                    <Button onClick={() => setStage(STAGES[stageIndex + 1]!.value)}>
                      Next: {STAGES[stageIndex + 1]!.label}
                    </Button>
                  ) : canRun ? (
                    <Button
                      disabled={saving || (status === 'active' && !dirty)}
                      onClick={() => void launch()}
                    >
                      {saving ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Rocket className="mr-2 h-4 w-4" />
                      )}
                      {launchLabel}
                    </Button>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      Publish it; someone who can run campaigns starts it.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Tabs>

          {textOpen && (
            <TextPanel
              spec={spec}
              names={filterNames}
              namesReady={filterNamesReady}
              focusedId={focusedSentenceId}
              onFocus={focusSentence}
              onSpecChange={setSpec}
            />
          )}
        </div>
      </div>

      {campaignId && (
        <VersionsDialog
          campaignId={campaignId}
          open={versionsOpen}
          onOpenChange={setVersionsOpen}
          onRestore={(restored) => {
            setSpec(restored);
            setVersionsOpen(false);
          }}
        />
      )}
    </div>
  );
}
